import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { toast } from "sonner";
import { Users, Gift, Copy, Send, Trophy, Sparkles } from "lucide-react";

export default function Referrals() {
  const { user, lang } = useApp();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); loadLeaderboard(); }, []);

  const load = async () => {
    try {
      const r = await api.get("/referrals/my");
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const loadLeaderboard = async () => {
    try {
      const r = await api.get("/referrals/leaderboard");
      setLeaderboard(r.data);
    } catch {}
  };

  const applyCode = async () => {
    if (!code.trim()) return toast.error("Enter a code");
    setBusy(true);
    try {
      const r = await api.post("/referrals/apply", { code: code.trim().toUpperCase() });
      toast.success(`+${r.data.reward_gc.toLocaleString()} GC from ${r.data.referrer_email}`);
      setCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const referralUrl = data ? `${window.location.origin}/register?ref=${data.code}` : "";
  const copyLink = () => {
    navigator.clipboard.writeText(referralUrl);
    toast.success(lang === "hu" ? "Link másolva!" : "Link copied!");
  };
  const copyCode = () => {
    navigator.clipboard.writeText(data?.code || "");
    toast.success(lang === "hu" ? "Kód másolva!" : "Code copied!");
  };
  const share = () => {
    const text = lang === "hu"
      ? `Csatlakozz hozzám a Havana Casinóban és 5,000 GC üdvözlő bónuszt kapsz! Kód: ${data?.code}`
      : `Join me on Havana Casino and get 5,000 GC welcome bonus! Code: ${data?.code}`;
    if (navigator.share) {
      navigator.share({ title: "Havana Casino", text, url: referralUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${text} ${referralUrl}`);
      toast.success(lang === "hu" ? "Szöveg másolva!" : "Share text copied!");
    }
  };

  const t = (hu, en) => (lang === "hu" ? hu : en);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Gift className="text-[#d4af37]" size={32}/>
        <h1 className="font-serif text-4xl">{t("Hívj meg egy barátot", "Refer a Friend")}</h1>
      </div>

      {/* Reward summary */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="lux-card p-5 text-center">
          <Sparkles className="text-[#d4af37] mx-auto" size={22}/>
          <div className="text-xs uppercase text-white/50 mt-2">{t("Neked", "You get")}</div>
          <div className="font-serif text-2xl text-[#d4af37] mt-1">{data ? `${data.signup_reward_referrer.toLocaleString()} GC` : "…"}</div>
          <div className="text-[10px] text-white/40 mt-1">{t("minden barátod regisztrációjakor", "per friend signup")}</div>
        </div>
        <div className="lux-card p-5 text-center">
          <Users className="text-[#d4af37] mx-auto" size={22}/>
          <div className="text-xs uppercase text-white/50 mt-2">{t("Neki jár", "They get")}</div>
          <div className="font-serif text-2xl text-emerald-400 mt-1">{data ? `${data.signup_reward_referee.toLocaleString()} GC` : "…"}</div>
          <div className="text-[10px] text-white/40 mt-1">{t("üdvözlő bónusz", "welcome bonus")}</div>
        </div>
        <div className="lux-card p-5 text-center">
          <Trophy className="text-[#d4af37] mx-auto" size={22}/>
          <div className="text-xs uppercase text-white/50 mt-2">{t("Első vásárlás után", "First purchase")}</div>
          <div className="font-serif text-2xl text-[#d4af37] mt-1">{data ? `${Math.round(data.purchase_reward_pct*100)}%` : "…"}</div>
          <div className="text-[10px] text-white/40 mt-1">{t("visszakapod jutalékként", "you earn as commission")}</div>
        </div>
      </div>

      {/* My code */}
      <div className="lux-card p-6 mb-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{background:"radial-gradient(circle at 20% 20%, rgba(212,175,55,.3), transparent 60%)"}}/>
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{t("A saját kódod", "Your code")}</div>
          <div className="flex flex-wrap items-center gap-3">
            <div data-testid="my-referral-code"
                 className="font-serif text-4xl gold-text tracking-widest bg-black/40 border border-[#d4af37]/40 rounded-lg px-6 py-3">
              {data?.code || "—"}
            </div>
            <button data-testid="copy-code" onClick={copyCode} className="btn-ghost px-4 py-3 rounded-md text-xs flex items-center gap-2">
              <Copy size={14}/>{t("Kód másolása", "Copy code")}
            </button>
            <button data-testid="copy-link" onClick={copyLink} className="btn-ghost px-4 py-3 rounded-md text-xs flex items-center gap-2">
              <Copy size={14}/>{t("Link másolása", "Copy link")}
            </button>
            <button data-testid="share-referral" onClick={share} className="btn-gold px-4 py-3 rounded-md text-xs flex items-center gap-2">
              <Send size={14}/>{t("Megosztás", "Share")}
            </button>
          </div>
          <div className="text-xs text-white/40 mt-3 break-all">
            {referralUrl}
          </div>
        </div>
      </div>

      {/* Apply code (only when user has never applied one) */}
      {data && data.total_referrals === 0 && !user?.referred_by && !data.referred_by && (
        <div className="lux-card p-5 mb-6">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{t("Van kódod egy baráttól?", "Got a code from a friend?")}</div>
          <div className="flex gap-2">
            <input data-testid="apply-code-input" value={code} onChange={e=>setCode(e.target.value.toUpperCase())}
                   placeholder="ABCD1234"
                   className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 uppercase focus:border-[#d4af37] focus:outline-none"/>
            <button data-testid="apply-code-submit" onClick={applyCode} disabled={busy}
                    className="btn-gold px-5 rounded text-sm">
              {busy ? "…" : t("Érvényesítés", "Apply")}
            </button>
          </div>
          <div className="text-[10px] text-white/40 mt-1">{t("A regisztrációt követő 24 órán belül használható.", "Must be applied within 24 hours of signup.")}</div>
        </div>
      )}

      {/* My referrals */}
      <div className="lux-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl flex items-center gap-2"><Users size={18} className="text-[#d4af37]"/>{t("Barátaim", "My referrals")} · {data?.total_referrals || 0}</h2>
          <div className="text-xs text-white/50">
            {t("Össz. kereset", "Total earned")}: <span className="text-[#d4af37]">{data?.total_earned_gc?.toLocaleString() || 0} GC</span>
            {data?.total_earned_currency > 0 && <span className="text-[#d4af37] ml-2">+ {data.total_earned_currency}</span>}
          </div>
        </div>
        {(data?.referrals || []).length === 0 ? (
          <div className="text-center text-white/40 text-sm py-6">
            {t("Még nincs meghívott barátod. Küldd el a linkedet!", "No referrals yet. Send your link!")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="text-[10px] uppercase text-white/50">
                <tr>
                  <th className="text-left p-2">Friend</th>
                  <th className="text-left p-2">Joined</th>
                  <th className="text-right p-2">Signup Reward</th>
                  <th className="text-right p-2">Purchase Reward</th>
                </tr>
              </thead>
              <tbody>
                {data.referrals.map(r=>(
                  <tr key={r.referee_id} className="border-t border-white/5">
                    <td className="p-2">{r.referee_email}</td>
                    <td className="p-2 text-xs text-white/50">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="p-2 text-right text-[#d4af37]">+{(r.signup_reward || 0).toLocaleString()} GC</td>
                    <td className="p-2 text-right text-[#d4af37]">{r.purchase_reward ? `+${r.purchase_reward} ${r.purchase_currency}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="lux-card p-6">
          <h2 className="font-serif text-xl flex items-center gap-2 mb-4"><Trophy size={18} className="text-[#d4af37]"/>{t("Top ajánlók", "Top Referrers")}</h2>
          <div className="space-y-1">
            {leaderboard.slice(0, 10).map((u, i)=>(
              <div key={u.referral_code} className="flex items-center gap-3 p-2 rounded hover:bg-white/5">
                <div className="w-6 text-center font-serif text-[#d4af37]">{i+1}</div>
                <div className="flex-1 text-sm">{u.name || u.email}</div>
                <div className="text-sm text-[#d4af37]">{u.referral_count} referrals</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
